import TextEditor from "@/components/TextEditor";
import { userRole } from "@/constant";
import { globalModalProps } from "@/constant/globalConstant";
import { useCreateSupportMutation, useUpdateSupportMutation } from "@/redux/api/supportApi";
import { useUploadFileMutation } from "@/redux/mainApi";
import { PlusOutlined } from "@ant-design/icons";
import { App, Button, Form, Input, message, Select, Upload } from "antd";
import PropTypes from "prop-types";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

const uploadButton = (
  <button style={{ border: 0, background: "none" }} type="button">
    <PlusOutlined />
    <div style={{ marginTop: 8 }}>Upload</div>
  </button>
);

const SupportForm = ({ mode = "create" }) => {
  // hooks
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { notification, modal } = App.useApp();
  const { id } = useParams();
  const user = useSelector((state) => state.user?.userDetails) || {};
  // state
  const [fileList, setFileList] = useState([]);

  // rtk query
  const [upload, { isLoading: uploadLoading }] = useUploadFileMutation();
  const [create, { isLoading: createLoading }] = useCreateSupportMutation();
  const [update, { isLoading: updateLoading }] = useUpdateSupportMutation();

  const validateFile = (file) => {
    const isImage = ["image/png", "image/jpeg", "image/jpg"].includes(file.type);
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isImage) {
      message.error("Only PNG, JPG, JPEG files are allowed.");
      return Upload.LIST_IGNORE;
    }
    if (!isLt5M) {
      message.error("File must be smaller than 5MB.");
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleFileUpload = async (file) => {
    if (!validateFile(file)) {
      notification.error({ message: "File validation failed.", duration: 2, showProgress: true });
      return false;
    }

    const tempFile = {
      uid: file.uid,
      name: file.name,
      status: "uploading",
      percent: 99,
      thumbUrl: "",
    };

    setFileList((prev) => [...prev, tempFile]);

    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await upload(formData).unwrap();

      setFileList((prev) =>
        prev.map((f) =>
          f.uid === file.uid
            ? {
                ...f,
                status: "done",
                url: data.url,
                thumbUrl: data.url,
              }
            : f,
        ),
      );
    } catch {
      // Mark file as error
      setFileList((prev) => prev.map((f) => (f.uid === file.uid ? { ...f, status: "error" } : f)));
      notification.error({ message: "Upload failed.", duration: 2 });
    }

    return false;
  };

  const handleSubmit = async (values) => {
    const body = { ...values, files: fileList };
    if (body.files?.length === 0) delete body.files;
    if (body?.user) delete body.user;

    await modal.confirm({
      ...globalModalProps,
      content: `Do you want to ${mode === "edit" ? "update" : "create"} this support ticket?`,
      onOk: async () => {
        try {
          const res = mode === "edit" ? await update({ id, body }).unwrap() : await create(body).unwrap();

          if (res?.success) {
            notification.success({
              message: `Support ticket successfully sent`,
              duration: 2,
              showProgress: true,
            });
            if (user?.role !== userRole.superAdmin) navigate("/support-inbox");
          }
        } catch (error) {
          notification.error({
            message: error?.data?.message || "Something went wrong. Please try again.",
            duration: 2,
            showProgress: true,
          });
        } finally {
          setFileList([]);
        }
      },
    });
  };

  const loading = createLoading || updateLoading;

  return (
    <div className="support-form">
      <h2 className="fs-5 fw-semibold mb-4">Submit a Support Request</h2>

      <Form form={form} layout="horizontal" onFinish={handleSubmit}>
        <Form.Item label="Title / Subject" name="title" rules={[{ required: true, message: "Please enter a title" }]}>
          <Input placeholder="Enter support title" />
        </Form.Item>

        <Form.Item label="Support Type" name="type" rules={[{ required: true, message: "Please select a support type" }]}>
          <Select
            placeholder="Select Support Type"
            options={[
              { value: "technical", label: "Software Bugs & Errors (Technical Department)" },
              { value: "administrative", label: "Changes to My Account (Administrative Department)" },
              { value: "finance", label: "Payment Related Issues (Finance Department)" },
              { value: "others", label: "Others" },
            ]}
          />
        </Form.Item>

        <Form.Item
          label="Message"
          getValueFromEvent={(content) => content}
          name="message"
          rules={[{ required: true, message: "Please enter your message" }]}
        >
          <TextEditor placeholder="Write your message here..." />
        </Form.Item>

        <Form.Item label="Attach Files">
          <Upload
            multiple
            accept=".png,.jpg,.jpeg"
            listType="picture-card"
            fileList={fileList}
            beforeUpload={handleFileUpload}
            onRemove={(file) => {
              setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
            }}
          >
            {fileList.length >= 8 ? null : uploadButton}
          </Upload>
          <p className="small text-secondary mt-2">Notice: You can select multiple files. Only images (PNG, JPG, JPEG) under 5MB are acceptable.</p>
        </Form.Item>

        <div className="d-flex justify-content-end gap-2">
          <Button type="primary" disabled={loading || uploadLoading} htmlType="submit">
            Send
          </Button>
          <Button onClick={() => navigate(-1)}>Back</Button>
        </div>
      </Form>
    </div>
  );
};

export default SupportForm;

SupportForm.propTypes = {
  mode: PropTypes.string,
};
